'use client'
import { useUser } from '@/hooks/use-user'
import s from './sign-in-form.module.scss'
import { useState } from 'react'
import Link from 'next/link'
import { useFormik } from 'formik'
import { ROUTES } from '@/constants'
import { toast } from 'sonner'
import Input from '@/components/v3/atoms/input/input'
import { PadlockIcon } from '@/components/svg/padlock-icon/padlock-icon'
import { ShowIcon } from '@/components/svg/show-icon/show-icon'
import { HideIcon } from '@/components/svg/hide-icon/hide-icon'
import { ButtonV4 } from '@/components/v4/atoms/button/button'
import * as Yup from 'yup'
import cx from 'classnames'
import { SocialAuthButton } from '@/components/v2/molecules/social-auth-button/social-auth-button'
import { useModal } from '@/hooks/use-modal'
import { useSignUpModal } from '@/hooks/use-sign-up-modal'

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
})

type Props = {
  redirect?: string
  modal?: boolean
}

export const SignInForm = ({ redirect, modal }: Props) => {
  const [showPassword, setShowPassword] = useState(false)
  const { logIn } = useUser()
  const modalContext = useModal()
  const signUpModal = useSignUpModal()

  const onSuccess = modalContext.options.modalProps?.onSuccess as
    | (() => void)
    | undefined

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const [err] = await logIn(values.email.trim(), values.password, {
        redirect: modal
          ? { disabled: true }
          : {
              reader: redirect ? redirect : ROUTES.READER.DASHBOARD,
              supplier: redirect ? redirect : ROUTES.SUPPLIER.DASHBOARD.SUMMARY,
              admin: redirect ? redirect : ROUTES.ADMIN.ROOT,
            },
      })
      if (err) {
        toast.error(err?.message || 'Something went wrong')
      } else if (modal) {
        modalContext.close()
        onSuccess?.()
      }
    },
  })

  const handleCreateAccountClick = async () => {
    await modalContext.close()
    await signUpModal.open()
  }

  return (
    <main className={cx(s.container, { [s.modal]: modal })}>
      {modal ? (
        <h2 className={cx(s.title, s.modalTitle)}>Sign In</h2>
      ) : (
        <h1 className={s.title}>Sign In</h1>
      )}
      <div className={cx(s.socialAuthButtons, { [s.modalSocialAuth]: modal })}>
        <SocialAuthButton type="google" className={s.socialAuthButton} />
        <SocialAuthButton type="facebook" className={s.socialAuthButton} />
      </div>
      <form onSubmit={formik.handleSubmit} className={s.signInForm} noValidate>
        <p>Or log in with email:</p>
        <Input
          label="Email"
          id="email"
          type="email"
          autoComplete="username"
          {...formik.getFieldProps('email')}
          error={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : undefined
          }
        />

        <Input
          autoComplete="current-password"
          label="Password"
          id="password"
          {...formik.getFieldProps('password')}
          type={showPassword ? 'text' : 'password'}
          leftIcon={<PadlockIcon />}
          rightIcon={
            <button
              className={s.showPasswordButton}
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <HideIcon /> : <ShowIcon />}
            </button>
          }
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : undefined
          }
        />

        <div className={s.forgotPasswordContainer}>
          <span className={s.forgotPassword}>Forgot your password?</span>{' '}
          <Link
            className={s.forgotPasswordLink}
            href={ROUTES.AUTH.FORGOTTEN_PASSWORD}
          >
            Send recovery link
          </Link>
        </div>

        <ButtonV4
          disabled={formik.isSubmitting}
          type="submit"
          variant="dark"
          className={s.signInButton}
        >
          {formik.isSubmitting ? 'SIGNING IN...' : 'SIGN IN'}
        </ButtonV4>
      </form>
      <p className={s.footer}>
        New to Rock My Wedding?{' '}
        {modal ? (
          <button
            className={s.createAccountButton}
            onClick={handleCreateAccountClick}
          >
            Create an account
          </button>
        ) : (
          <Link className={s.createAccountLink} href={ROUTES.AUTH.SIGN_UP}>
            Create an account
          </Link>
        )}
      </p>
    </main>
  )
}
